import { SearchIcon } from 'lucide-react';
import { type JSX, type ReactNode, useEffect, useState } from 'react';

import { Input } from '@/components/ui/input.js';
import type { SharePointItem } from '@/lib/api/sharepoint.js';

import {
  SharePointBreadcrumb,
  SharePointDriveList,
  SharePointItemList,
  SharePointSearchNote,
  SharePointSearchResultList,
  SharePointSiteList,
  type BrowseCrumb,
} from './BrowseLevelList.js';
import { isSharePointNotConnected } from './browse-error.js';
import { useSharePointLevels } from './browse-levels.js';
import { SEARCH_DEBOUNCE_MS, useSharePointFileSearch } from './file-search.js';
import type { SharePointKnowledgeMark } from './knowledge-mark.js';
import {
  NO_REMEMBERED_PATH,
  type SharePointRememberedPath,
  type SharePointRememberedPathState,
} from './remembered-path.js';
import { SharePointNotConnectedNotice } from './SharePointNotConnected.js';
import {
  treeItemRow,
  type SharePointFileSelection,
  type SharePointRow,
} from './sharepoint-row.js';
import { resolveFolderTrail } from './sharepoint-trail.js';
import type { SharePointTreePosition } from './tree-position.js';

/**
 * The label of the tree's root when the caller did not name the connection — the one the service
 * resolves for the workspace.
 *
 * The root is a CONNECTION, not the site list. A second connection (another tenant) is a second root
 * beside this one rather than a rewrite of the tree, which is why a caller that knows which connection
 * it is browsing passes its name and its id: the crumb then says which SharePoint the user is standing
 * in, and every browse call and cache key names it.
 */
const CONNECTION_LABEL = 'SharePoint';

/**
 * The workspace's SharePoint tree, from the connection root down through sites, document libraries and
 * folders to the files themselves. Reading it needs nothing but workspace membership — the boundary is
 * the workspace, not the caller's own SharePoint rights, because every call runs on the workspace's
 * shared connection.
 *
 * It is one component for both surfaces that walk the tree: the Artifacts section's tab, which keeps the
 * position in the URL, and the composer's "Add artifacts" dialog, which keeps it in its own state.
 * Whatever a surface offers per row comes in through {@link renderItemAction}, and picking files comes in
 * through {@link fileSelection}, so neither can grow a navigation quirk the other does not have. Both are
 * handed a {@link SharePointRow} — the item WITH the site and library it lives in — because a row found by
 * searching comes from anywhere the connection reaches, and a surface that read the address off its own
 * position would build the wrong reference for it.
 *
 * Searching lives here for the surfaces that browse ONE named SharePoint — the knowledge wizard and the
 * transfer screen, where a person is choosing a folder in a SharePoint they have already named, and a
 * search reaching past it would be noise rather than help. It replaces the level on screen rather than
 * filtering it, because it is Microsoft Search across everything the connection reaches, and clearing the
 * box puts the user back on the level they were on, untouched.
 *
 * A surface that searches every connection at once owns its own box instead and passes
 * {@link hostOwnsSearch}: the fan is wider than any one tree, so it cannot live inside one. This tree then
 * draws no box and no results and is only a tree.
 *
 * The position is the caller's ({@link SharePointTreePosition}); names are not. The URL carries ids, so a
 * pasted link shows a generic label for a level nobody in this session walked through. That is
 * deliberate — the alternative is putting display names in a shareable URL, where they go stale the
 * moment someone renames a folder.
 */
export function SharePointBrowser({
  workspaceId,
  connectionId = null,
  connectionLabel,
  position,
  onNavigate,
  hostOwnsSearch = false,
  rememberedPath,
  renderItemAction,
  knowledgeMark,
  fileSelection,
  onOpenFile,
}: {
  workspaceId: string;
  /**
   * Which of the workspace's SharePoint connections this tree is. `null` leaves the choice to the
   * service, which resolves the workspace's shared connection — what a surface showing a single tree
   * wants, and what every caller did before connections were named. It travels into the browse calls,
   * into their cache keys and into the knowledge mark, because a Graph id means nothing outside the
   * tenant it came from.
   */
  connectionId?: string | null;
  /** The name of that connection for the root crumb; the generic word when the caller named none. */
  connectionLabel?: string;
  position: SharePointTreePosition;
  onNavigate: (position: SharePointTreePosition) => void;
  /**
   * Set by a surface that searches every connection it reaches from a box of its own. This tree then shows
   * neither box nor results, and is only a tree. Left off, searching stays here and behaves as it always
   * has — one box, one connection, inside the tree.
   */
  hostOwnsSearch?: boolean;
  /**
   * The walked-through names, held by the caller. A surface that takes this tree OFF screen has to own them
   * or they die with it — which is what happens on the Artifacts tab, where a search replaces the tree and
   * clearing the box would otherwise put the user back on "Site / Library". Left off, the tree keeps its own
   * state and behaves exactly as it always has, which is what an always-mounted tree wants.
   */
  rememberedPath?: SharePointRememberedPathState;
  renderItemAction?: (row: SharePointRow) => ReactNode;
  /**
   * Whether an item is already a workspace knowledge source. It is answered per ADDRESS, so this one
   * predicate serves the tree and the results alike — the tree's rows take the address of the library the
   * user is standing in, a result carries its own — and neither list can grow a rule the other lacks.
   */
  knowledgeMark?: SharePointKnowledgeMark;
  /** Set by a surface whose file rows are pick toggles rather than links to something else. */
  fileSelection?: SharePointFileSelection;
  /**
   * Set by a surface that opens a clicked file in the file viewer. The click still points the URL at the
   * file, as picking one always has, and then hands the file's row up to be opened.
   */
  onOpenFile?: (row: SharePointRow) => void;
}): JSX.Element {
  const ownRememberedPath =
    useState<SharePointRememberedPath>(NO_REMEMBERED_PATH);
  const [remembered, setRemembered] = rememberedPath ?? ownRememberedPath;
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * Results are on screen only while the box HAS something in it and that something has settled into a
   * query. Reading both is what makes clearing the box instant — waiting for the debounce would leave the
   * old results up for another quarter second — while typing keeps the tree until there is something to
   * put in its place, rather than blinking through an empty results panel.
   */
  const results = useSharePointFileSearch(workspaceId, query, connectionId);
  const searching =
    !hostOwnsSearch && searchInput.trim() !== '' && query !== '';

  const folderTrail = resolveFolderTrail(remembered.folders, position.folderId);
  const levels = useSharePointLevels({
    workspaceId,
    connectionId,
    siteId: position.siteId,
    driveId: position.driveId,
    parent: position.folderId ?? 'root',
    enabled: true,
  });
  const activeList =
    position.siteId === null
      ? levels.sites
      : position.driveId === null
        ? levels.drives
        : levels.items;

  const siteName =
    remembered.site !== null && remembered.site.id === position.siteId
      ? remembered.site.name
      : null;
  const driveName =
    remembered.drive !== null && remembered.drive.id === position.driveId
      ? remembered.drive.name
      : null;

  const crumbs: BrowseCrumb[] = [
    {
      key: 'connection',
      label: connectionLabel ?? CONNECTION_LABEL,
      onSelect: () =>
        onNavigate({
          siteId: null,
          driveId: null,
          folderId: null,
          itemId: null,
        }),
    },
  ];
  if (position.siteId !== null) {
    crumbs.push({
      key: position.siteId,
      label: siteName ?? 'Site',
      onSelect: () =>
        onNavigate({
          siteId: position.siteId,
          driveId: null,
          folderId: null,
          itemId: null,
        }),
    });
  }
  if (position.driveId !== null) {
    crumbs.push({
      key: position.driveId,
      label: driveName ?? 'Library',
      onSelect: () =>
        onNavigate({
          siteId: position.siteId,
          driveId: position.driveId,
          folderId: null,
          itemId: null,
        }),
    });
  }
  for (const crumb of folderTrail) {
    crumbs.push({
      key: crumb.itemId,
      label: crumb.name ?? 'Folder',
      onSelect: () =>
        onNavigate({
          siteId: position.siteId,
          driveId: position.driveId,
          folderId: crumb.itemId,
          itemId: null,
        }),
    });
  }

  /**
   * The place the rows of the open level are at, or null when the tree is above a library and there are no
   * file rows to act on. It is what turns an item into a {@link SharePointRow}: search results carry their
   * own address, tree rows take the one they were listed at.
   */
  const place =
    position.siteId !== null && position.driveId !== null
      ? {
          connectionId,
          siteId: position.siteId,
          driveId: position.driveId,
          folderId: position.folderId,
        }
      : null;

  /** Point the URL at a file of the open folder, which highlights it in the list. */
  const pointAtFile = (item: SharePointItem): void =>
    onNavigate({
      siteId: position.siteId,
      driveId: position.driveId,
      folderId: position.folderId,
      itemId: item.itemId,
    });

  if (
    isSharePointNotConnected(activeList.error) ||
    isSharePointNotConnected(results.error)
  ) {
    return <SharePointNotConnectedNotice workspaceId={workspaceId} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {!hostOwnsSearch && (
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search files by name or contents"
            aria-label="Search SharePoint files"
            autoComplete="off"
            className="pl-9"
          />
        </div>
      )}

      {searching ? (
        <SharePointSearchNote />
      ) : (
        <SharePointBreadcrumb crumbs={crumbs} />
      )}

      <div className="flex flex-col gap-1 rounded-xl border border-border p-2">
        {searching && (
          <SharePointSearchResultList
            list={results}
            connectionId={connectionId}
            onOpenFolder={(hit) => {
              setSearchInput('');
              setQuery('');
              setRemembered(NO_REMEMBERED_PATH);
              onNavigate({
                siteId: hit.siteId,
                driveId: hit.driveId,
                folderId: hit.itemId,
                itemId: null,
              });
            }}
            onOpenFile={onOpenFile}
            renderItemAction={renderItemAction}
            knowledgeMark={
              knowledgeMark
                ? (row) =>
                    knowledgeMark({
                      connectionId: row.connectionId,
                      driveId: row.driveId,
                      itemId: row.item.itemId,
                    })
                : undefined
            }
            fileSelection={fileSelection}
          />
        )}
        {!searching && position.siteId === null && (
          <SharePointSiteList
            list={levels.sites}
            onPick={(site) => {
              setRemembered({
                site: { id: site.siteId, name: site.name },
                drive: null,
                folders: [],
              });
              onNavigate({
                siteId: site.siteId,
                driveId: null,
                folderId: null,
                itemId: null,
              });
            }}
          />
        )}
        {!searching &&
          position.siteId !== null &&
          position.driveId === null && (
            <SharePointDriveList
              list={levels.drives}
              onPick={(drive) => {
                setRemembered((path) => ({
                  ...path,
                  drive: { id: drive.driveId, name: drive.name },
                  folders: [],
                }));
                onNavigate({
                  siteId: position.siteId,
                  driveId: drive.driveId,
                  folderId: null,
                  itemId: null,
                });
              }}
            />
          )}
        {!searching && position.driveId !== null && (
          <SharePointItemList
            list={levels.items}
            selectedItemId={position.itemId}
            onOpenFolder={(item) => {
              setRemembered((path) => ({
                ...path,
                folders: [
                  ...folderTrail,
                  { itemId: item.itemId, name: item.name },
                ],
              }));
              onNavigate({
                siteId: position.siteId,
                driveId: position.driveId,
                folderId: item.itemId,
                itemId: null,
              });
            }}
            onSelectFile={pointAtFile}
            onOpenFile={
              place && onOpenFile
                ? (item) => {
                    pointAtFile(item);
                    onOpenFile(treeItemRow(place, item));
                  }
                : undefined
            }
            renderItemAction={
              place && renderItemAction
                ? (item) => renderItemAction(treeItemRow(place, item))
                : undefined
            }
            knowledgeMark={
              place && knowledgeMark
                ? (item) =>
                    knowledgeMark({
                      connectionId,
                      driveId: place.driveId,
                      itemId: item.itemId,
                    })
                : undefined
            }
            fileSelection={
              place && fileSelection
                ? {
                    isSelected: (item) =>
                      fileSelection.isSelected(treeItemRow(place, item)),
                    toggle: (item) =>
                      fileSelection.toggle(treeItemRow(place, item)),
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
